import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NavigationHistoryService {
  private history: string[] = [];

  constructor(private router: Router) {
    // Track navigation history
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const newUrl = event.urlAfterRedirects;
        const newBasePath = this.getBasePath(newUrl);
        const currentUrl = this.history[this.history.length - 1];

        // Same base path — update in place (query param change only)
        if (currentUrl && newBasePath === this.getBasePath(currentUrl)) {
          this.history[this.history.length - 1] = newUrl;
          return;
        }

        // Check if this URL exists earlier in history (back navigation)
        // Truncate forward entries instead of pushing — mirrors real browser behaviour
        const existingIndex = this.findLastIndex(newBasePath);
        if (existingIndex !== -1) {
          this.history = this.history.slice(0, existingIndex + 1);
          this.history[existingIndex] = newUrl; // preserve updated query params
        } else {
          // Genuinely new page — push
          this.history.push(newUrl);
          if (this.history.length > 10) {
            this.history.shift();
          }
        }
      });
  }

  /**
   * Extract base path without query params or fragments
   */
  private getBasePath(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  /**
   * Find the last occurrence of a base path in history, excluding the current (last) entry.
   */
  private findLastIndex(basePath: string): number {
    for (let i = this.history.length - 2; i >= 0; i--) {
      if (this.getBasePath(this.history[i]) === basePath) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Get the previous URL (excluding current)
   */
  getPreviousUrl(): string | null {
    if (this.history.length > 1) {
      // Return second-to-last URL (last is current page)
      return this.history[this.history.length - 2];
    }
    return null;
  }

  /**
   * Get URL before the previous one (useful if previous is a detail page)
   */
  getUrlBeforePrevious(): string | null {
    if (this.history.length > 2) {
      return this.history[this.history.length - 3];
    }
    return null;
  }

  /**
   * Get the full history
   */
  getHistory(): string[] {
    return [...this.history];
  }

  /**
   * Clear history
   */
  clearHistory(): void {
    this.history = [];
  }
}
